import { useState } from "react";
import { KeyRound, X } from "lucide-react";
import { useTranslation } from "@/lib/i18n/i18n";

/**
 * « Mot de passe oublié » de l'espace paroisse — même parcours que l'Économat
 * (apps/catho-economat/client/src/pages/login.tsx), servi par auth-service :
 *   1. POST /api/auth/forgot-password { contact } → code à 6 chiffres envoyé
 *      par courriel (adresse) ou par SMS (téléphone). Réponse neutre, que le
 *      compte existe ou non.
 *   2. POST /api/auth/reset-password { contact, code, newPassword,
 *      confirmPassword } → nouveau mot de passe, toutes les sessions fermées.
 * Le bouton n'avait jusqu'ici aucune action.
 */
export function ForgotPasswordDialog({
  initialContact,
  onClose,
  onDone,
}: {
  initialContact: string;
  onClose: () => void;
  /** Appelé après une réinitialisation réussie, avec l'identifiant utilisé. */
  onDone: (contact: string) => void;
}) {
  const t = useTranslation();
  const [step, setStep] = useState<"contact" | "code">("contact");
  const [contact, setContact] = useState(initialContact);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const post = async (path: string, body: Record<string, string>) => {
    const res = await fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as { message?: string };
    if (!res.ok) throw new Error(data.message || `${t("auth.forgot.errorGeneric")} (${res.status})`);
    return data;
  };

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const data = await post("/api/auth/forgot-password", { contact: contact.trim() });
      setInfo(data.message || t("auth.forgot.codeSent"));
      setStep("code");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError(t("auth.forgot.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await post("/api/auth/reset-password", {
        contact: contact.trim(),
        code: code.trim(),
        newPassword,
        confirmPassword,
      });
      onDone(contact.trim());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm text-brand-primary dark:text-gray-100 placeholder:text-gray-300 focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent/20";
  const labelClass = "block text-xs font-medium text-brand-primary dark:text-gray-200 mb-1.5";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="forgot-title"
      data-testid="dialog-forgot-password"
    >
      <div className="w-full max-w-[420px] bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-brand-accent/10 flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-brand-accent" aria-hidden="true" />
            </div>
            <h2 id="forgot-title" className="text-sm font-bold text-brand-primary dark:text-gray-100">
              {t("auth.forgot.title")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("auth.forgot.close")}
            data-testid="btn-forgot-close"
            className="text-gray-300 hover:text-gray-500 transition-colors"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          {step === "contact" ? t("auth.forgot.introContact") : t("auth.forgot.introCode")}
        </p>

        {step === "contact" ? (
          <form onSubmit={requestCode} className="space-y-3">
            <div>
              <label htmlFor="forgot-contact" className={labelClass}>{t("auth.forgot.contactLabel")}</label>
              <input
                id="forgot-contact"
                required
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder={t("auth.forgot.contactPlaceholder")}
                data-testid="input-forgot-contact"
                className={inputClass}
              />
            </div>
            {error && (
              <p className="text-xs text-red-600 bg-red-50 px-4 py-2.5 rounded-xl" data-testid="forgot-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading || contact.trim().length < 3}
              data-testid="btn-forgot-submit"
              className="w-full py-3 rounded-xl font-semibold text-sm text-white disabled:opacity-60 transition-all"
              style={{ backgroundColor: "var(--brand-orange)" }}
            >
              {loading ? t("auth.forgot.sending") : t("auth.forgot.sendCode")}
            </button>
            <p className="text-[11px] text-gray-400" data-testid="forgot-support">{t("auth.forgot.support")}</p>
          </form>
        ) : (
          <form onSubmit={resetPassword} className="space-y-3">
            {info && (
              <p className="text-xs text-brand-primary bg-brand-primary/5 px-4 py-2.5 rounded-xl" data-testid="forgot-info">{info}</p>
            )}
            <div>
              <label htmlFor="forgot-code" className={labelClass}>{t("auth.forgot.codeLabel")}</label>
              <input
                id="forgot-code"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                data-testid="input-forgot-code"
                className={`${inputClass} tracking-[0.3em]`}
              />
            </div>
            <div>
              <label htmlFor="forgot-new-password" className={labelClass}>{t("auth.forgot.newPassword")}</label>
              <input
                id="forgot-new-password"
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="input-forgot-new-password"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="forgot-confirm-password" className={labelClass}>{t("auth.forgot.confirmPassword")}</label>
              <input
                id="forgot-confirm-password"
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="input-forgot-confirm-password"
                className={inputClass}
              />
            </div>
            {error && (
              <p className="text-xs text-red-600 bg-red-50 px-4 py-2.5 rounded-xl" data-testid="forgot-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading || code.length !== 6 || newPassword.length < 8}
              data-testid="btn-forgot-reset"
              className="w-full py-3 rounded-xl font-semibold text-sm text-white disabled:opacity-60 transition-all"
              style={{ backgroundColor: "var(--brand-orange)" }}
            >
              {loading ? t("auth.forgot.saving") : t("auth.forgot.save")}
            </button>
            <button
              type="button"
              onClick={() => { setStep("contact"); setError(""); }}
              className="w-full text-xs text-brand-info-blue hover:underline"
              data-testid="btn-forgot-resend"
            >
              {t("auth.forgot.resend")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
